/**
 * "Add to calendar" helpers — provider-agnostic, no OAuth.
 *
 * Builds pre-filled deep links for the web calendar providers and generates a
 * downloadable .ics for everything else (Apple Calendar, desktop Outlook, etc.).
 * Meeting times are UTC ISO strings from the API; providers that need UTC get
 * the compact ``YYYYMMDDTHHMMSSZ`` form, and calendar apps localize on display.
 */

export interface CalEvent {
  title: string
  /** ISO-8601 (UTC) start, e.g. "2026-09-30T15:00:00Z". */
  startsAt: string
  /** ISO-8601 (UTC) end. */
  endsAt: string
  location?: string
  description?: string
}

export type CalProvider = 'google' | 'outlook' | 'yahoo' | 'ics'

export const PROVIDER_LABELS: Record<CalProvider, string> = {
  google: 'Google Calendar',
  outlook: 'Outlook',
  yahoo: 'Yahoo Calendar',
  ics: 'Download (.ics)',
}

export const CAL_PROVIDERS: CalProvider[] = ['google', 'outlook', 'yahoo', 'ics']

const PREF_KEY = 'vc:calendarProvider'

/** Compact UTC timestamp for Google/Yahoo/.ics: 20260930T150000Z. */
function toUtcCompact(iso: string): string {
  const d = new Date(iso)
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
}

export function googleCalendarUrl(e: CalEvent): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: e.title,
    dates: `${toUtcCompact(e.startsAt)}/${toUtcCompact(e.endsAt)}`,
  })
  if (e.location) params.set('location', e.location)
  if (e.description) params.set('details', e.description)
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

export function outlookCalendarUrl(e: CalEvent): string {
  // Outlook accepts ISO-8601; works for office.com and outlook.live.com.
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: e.title,
    startdt: new Date(e.startsAt).toISOString(),
    enddt: new Date(e.endsAt).toISOString(),
  })
  if (e.location) params.set('location', e.location)
  if (e.description) params.set('body', e.description)
  return `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`
}

export function yahooCalendarUrl(e: CalEvent): string {
  // Yahoo uses v=60, ST/ET in compact UTC, DUR is unused when ET is given.
  const params = new URLSearchParams({
    v: '60',
    title: e.title,
    st: toUtcCompact(e.startsAt),
    et: toUtcCompact(e.endsAt),
  })
  if (e.location) params.set('in_loc', e.location)
  if (e.description) params.set('desc', e.description)
  return `https://calendar.yahoo.com/?${params.toString()}`
}

function icsEscape(text: string | undefined): string {
  if (!text) return ''
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n')
}

export function icsContent(e: CalEvent): string {
  const stamp = toUtcCompact(new Date().toISOString())
  const uid = `${stamp}-${Math.random().toString(36).slice(2)}@vericast-events`
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Vericast Events//Meetings//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${toUtcCompact(e.startsAt)}`,
    `DTEND:${toUtcCompact(e.endsAt)}`,
    `SUMMARY:${icsEscape(e.title)}`,
  ]
  if (e.description) lines.push(`DESCRIPTION:${icsEscape(e.description)}`)
  if (e.location) lines.push(`LOCATION:${icsEscape(e.location)}`)
  lines.push('END:VEVENT', 'END:VCALENDAR')
  return lines.join('\r\n') + '\r\n'
}

/** Trigger a browser download of the event as an .ics file. */
export function downloadIcs(e: CalEvent, filename = 'meeting.ics'): void {
  const blob = new Blob([icsContent(e)], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/** Open (or download for .ics) the event in the given provider. */
export function addToCalendar(provider: CalProvider, e: CalEvent): void {
  if (provider === 'ics') {
    downloadIcs(e)
    return
  }
  const url =
    provider === 'google'
      ? googleCalendarUrl(e)
      : provider === 'outlook'
      ? outlookCalendarUrl(e)
      : yahooCalendarUrl(e)
  window.open(url, '_blank', 'noopener')
}

/** The attendee's last-used provider (defaults to Google). */
export function getPreferredProvider(): CalProvider {
  try {
    const v = localStorage.getItem(PREF_KEY)
    if (v && (CAL_PROVIDERS as string[]).includes(v)) return v as CalProvider
  } catch {
    /* localStorage unavailable */
  }
  return 'google'
}

export function setPreferredProvider(p: CalProvider): void {
  try {
    localStorage.setItem(PREF_KEY, p)
  } catch {
    /* ignore */
  }
}
